<template>
  <div class="xts-product-overview-page">
    <a-breadcrumb style="margin: 16px 0px;">
      <a-breadcrumb-item>产品开发者</a-breadcrumb-item>
      <a-breadcrumb-item>概览</a-breadcrumb-item>
      <a-breadcrumb-item>XTS 产品全景图</a-breadcrumb-item>
    </a-breadcrumb>

    <a-spin :spinning="summaryLoading">
      <section class="panel">
        <div class="panel-head">
          <div class="panel-actions">
            <span class="action-label">测评通过时间</span>
            <a-range-picker v-model:value="dateRange" :allow-clear="true" />
          </div>
          <div class="total-summary" v-if="totalSummary">
            <span class="total-summary-label">共</span>
            <span class="total-summary-item">
              <span class="total-summary-value">{{ formatNumber(totalSummary.dataCount) }}</span>
              <span class="total-summary-unit">款产品</span>
            </span>
            <span class="total-summary-divider">·</span>
            <span class="total-summary-item">
              <span class="total-summary-value">{{ formatNumber(totalSummary.companyCount) }}</span>
              <span class="total-summary-unit">家厂商（去重）</span>
            </span>
          </div>
        </div>

        <div class="key-grid">
          <article :class="['key-card', getThemeClass(item.type)]" v-for="item in keyBlocks" :key="item.type">
            <div class="key-card-head">{{ item.type }}</div>
            <div class="metric-pair">
              <div class="metric-row">
                <span class="metric-label">{{ item.productLabel }}</span>
                <span class="metric-value">{{ formatNumber(item.productCount) }}<span
                    class="metric-unit">款</span></span>
              </div>
              <div class="metric-row">
                <span class="metric-label">{{ item.vendorLabel }}</span>
                <span class="metric-value">{{ formatNumber(item.vendorCount) }}<span class="metric-unit">家</span></span>
              </div>
            </div>
          </article>
        </div>
      </section>
    </a-spin>

    <a-spin :spinning="detailLoading">
      <section class="panel">
        <div class="analysis-grid">
          <article :class="['analysis-card', 'analysis-card-device', 'key-card', getThemeClass('设备')]">
            <div class="card-title-row">
              <div class="key-card-head">设备</div>
              <div class="inline-filter">
                <span>设备所属行业</span>
                <a-select v-model:value="selectedIndustry" style="width: 180px">
                  <a-select-option v-for="item in deviceIndustryOptions" :key="item" :value="item"
                    style="text-align: center;">
                    {{ item }}
                  </a-select-option>
                </a-select>
              </div>
            </div>

            <div class="meta-inline">
              <span class="meta-prefix">当前行业</span>
              <span class="meta-industry">{{ selectedIndustry || '--' }}</span>
              <span class="meta-divider">·</span>
              <span class="meta-prefix">共</span>
              <span class="meta-count">{{ formatNumber(deviceIndustryCount) }}</span>
              <span class="meta-unit">款</span>
            </div>

            <div class="device-tables-row">
              <div class="table-wrap device-table-scroll">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>所属行业细分领域</th>
                      <th>总数</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in deviceSubIndustryRows" :key="row.name">
                      <td>{{ row.name }}</td>
                      <td>{{ row.total }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="table-wrap device-total-table-scroll">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>搭载操作系统类型</th>
                      <th>产品数量</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in deviceOsTotalsStatic" :key="item.osType">
                      <td>{{ item.osType }}</td>
                      <td>{{ item.count }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="chart-wrap">
              <my-bar-chart :categories="deviceSubIndustryChart.categories" :series="deviceSubIndustryChart.series"
                :colors="systemChartColors" :bar-width="deviceBarWidth" :x-axis-label-font-size="12"
                :x-axis-label-rotate="0" :grid-bottom="44" :height="220" :y-split-number="5" />
            </div>
          </article>

          <div class="analysis-grid-secondary">
            <article :class="['analysis-card', getThemeClass(block.type)]" v-for="block in typeAnalysisBlocks"
              :key="block.type">
              <div class="card-title-row">
                  <div class="key-card-head">{{ block.type }}</div>
                </div>

              <div class="table-wrap compact">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>搭载操作系统类型</th>
                      <th>产品数量</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in block.tableRows" :key="`${block.type}-${row.osType}`">
                      <td>{{ row.osType }}</td>
                      <td>{{ row.count }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="chart-wrap">
                <my-bar-chart :categories="osTypes" :values="block.values" :colors="systemChartColors" :bar-width="42"
                  :height="220" :y-split-number="5" />
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="panel">
        <div class="panel-head">
          <h2>搭载操作系统类型</h2>
        </div>

        <div class="os-total-layout">
          <div class="table-wrap os-total-table-wrap">
            <table class="data-table modern-summary-table">
              <thead>
                <tr>
                  <th>搭载操作系统类型</th>
                  <th>总数</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in osProductMatrix" :key="row.osType">
                  <td>{{ row.osType }}</td>
                  <td>{{ row.total }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <my-bar-chart :categories="osTypes" :series="osProductMatrixSeries" :colors="evalTypeChartColors"
            :horizontal="true" :bar-width="10" :height="320" :y-split-number="6" />
        </div>
      </section>
    </a-spin>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import dayjs from 'dayjs';
import axios from 'axios';
import { notification } from 'ant-design-vue';
import MyBarChart from '../components/MyBarChart.vue';

const osTypes = ['轻量', '小型', '标准'];
const systemChartColors = ['#4A90E2', '#2EB07A', '#E6A23C'];
const evalTypeChartColors = ['#4A90E2', '#2EB07A', '#8E6FD8', '#E6A23C'];

const summaryLoading = ref(false);
const detailLoading = ref(false);
const selectedIndustry = ref('');
const dateRange = ref([]);

const summaryStats = ref([]);
const detailStats = ref({
  systemTypeStats: [],
  industrySpecificCategoryStats: []
});

const keyBlockConfig = [
  { type: '设备', productLabel: '新增设备', vendorLabel: '设备厂商' },
  { type: '模组/开发板', productLabel: '新增模组/开发板', vendorLabel: '模组/开发板厂商' },
  { type: '芯片', productLabel: '新增芯片', vendorLabel: '芯片厂商' },
  { type: 'OSV', productLabel: '新增 OSV', vendorLabel: 'OSV 厂商' }
];

const normalizeText = (value) => {
  if (value == null) return '其他';
  const text = String(value).trim();
  if (!text) return '其他';
  if (text.toLowerCase && text.toLowerCase() === 'null') return '其他';
  return text || '其他';
};

const normalizeSystemType = (value) => {
  const text = String(value || '').trim().replace(/系统$/, '');
  return osTypes.includes(text) ? text : '';
};

const sortWithOtherLast = (values) => {
  const regular = values.filter((item) => item !== '其他').sort((a, b) => a.localeCompare(b, 'zh-CN'));
  if (values.includes('其他')) {
    regular.push('其他');
  }
  return regular;
};

const safeCount = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const getThemeClass = (type) => {
  if (type === '设备') return 'theme-device';
  if (type === '模组/开发板') return 'theme-module';
  if (type === '芯片') return 'theme-chip';
  if (type === 'OSV') return 'theme-osv';
  return 'theme-device';
};

const buildRangeParams = () => {
  const [startRaw, endRaw] = Array.isArray(dateRange.value) ? dateRange.value : [];
  if (!startRaw || !endRaw) {
    return {};
  }
  const start = dayjs(startRaw).startOf('day');
  const end = dayjs(endRaw).endOf('day');
  return {
    startTime: start.format('YYYY-MM-DDTHH:mm:ssZ'),
    endTime: end.format('YYYY-MM-DDTHH:mm:ssZ')
  };
};

const summaryByType = computed(() => {
  const index = {};
  summaryStats.value.forEach((item) => {
    index[item.certificationType] = {
      dataCount: safeCount(item.dataCount),
      companyCount: safeCount(item.companyCount)
    };
  });
  return index;
});

const totalSummary = computed(() => {
  const found = summaryStats.value.find((item) => item.certificationType === '总计');
  if (!found) return null;
  return {
    dataCount: safeCount(found.dataCount),
    companyCount: safeCount(found.companyCount)
  };
});

const keyBlocks = computed(() => keyBlockConfig.map((config) => {
  const found = summaryByType.value[config.type] || { dataCount: 0, companyCount: 0 };
  return {
    ...config,
    productCount: found.dataCount,
    vendorCount: found.companyCount
  };
}));

const validSystemTypeStats = computed(() => {
  return (detailStats.value.systemTypeStats || []).filter((item) => item?.certificationType);
});

const getTypeOsCount = (certificationType, osType) => {
  return validSystemTypeStats.value
    .filter((item) => item.certificationType === certificationType && normalizeSystemType(item.systemType) === osType)
    .reduce((sum, item) => sum + safeCount(item.dataCount), 0);
};

const deviceOsTotalsStatic = computed(() => {
  const totals = { 轻量: 0, 小型: 0, 标准: 0 };
  const selIndustry = selectedIndustry.value;
  (industryStats.value || []).forEach((item) => {
    if (normalizeText(item.industry) !== selIndustry) return;
    const osType = normalizeSystemType(item.systemType);
    if (osType && Object.prototype.hasOwnProperty.call(totals, osType)) {
      totals[osType] += safeCount(item.dataCount);
    }
  });

  return osTypes.map((osType) => ({
    osType,
    count: totals[osType] || 0
  }));
});

const industryStats = computed(() => detailStats.value.industrySpecificCategoryStats || []);

const deviceIndustryOptions = computed(() => {
  const options = Array.from(new Set(industryStats.value.map((item) => normalizeText(item.industry))));
  return sortWithOtherLast(options);
});

watch(
  deviceIndustryOptions,
  (options) => {
    if (!options.length) {
      selectedIndustry.value = '';
      return;
    }
    if (!options.includes(selectedIndustry.value)) {
      selectedIndustry.value = options[0];
    }
  },
  { immediate: true }
);

const deviceIndustryRows = computed(() => {
  return industryStats.value.filter((item) => normalizeText(item.industry) === selectedIndustry.value);
});

const deviceIndustryCount = computed(() => {
  return deviceIndustryRows.value.reduce((sum, item) => sum + safeCount(item.dataCount), 0);
});

const deviceSubIndustryRows = computed(() => {
  const grouped = new Map();
  deviceIndustryRows.value.forEach((item) => {
    const name = normalizeText(item.specificCategory);
    const osType = normalizeSystemType(item.systemType);
    const count = safeCount(item.dataCount);

    if (!grouped.has(name)) {
      grouped.set(name, {
        name,
        total: 0,
        osMap: { 轻量: 0, 小型: 0, 标准: 0 }
      });
    }

    const target = grouped.get(name);
    target.total += count;
    if (osType && target.osMap[osType] != null) {
      target.osMap[osType] += count;
    }
  });

  return Array.from(grouped.values()).sort((a, b) => {
    if (a.name === '其他' && b.name !== '其他') return 1;
    if (a.name !== '其他' && b.name === '其他') return -1;
    return b.total - a.total;
  });
});

const deviceSubIndustryChart = computed(() => ({
  categories: deviceSubIndustryRows.value.map((item) => item.name),
  series: [
    { name: '轻量', values: deviceSubIndustryRows.value.map((item) => item.osMap.轻量), color: systemChartColors[0] },
    { name: '小型', values: deviceSubIndustryRows.value.map((item) => item.osMap.小型), color: systemChartColors[1] },
    { name: '标准', values: deviceSubIndustryRows.value.map((item) => item.osMap.标准), color: systemChartColors[2] }
  ]
}));

const deviceBarWidth = computed(() => {
  const count = deviceSubIndustryRows.value.length;
  if (count <= 3) return 30;
  if (count <= 6) return 26;
  if (count <= 10) return 22;
  return 18;
});

const buildTypeRows = (type) => {
  return osTypes.map((osType) => ({
    osType,
    count: getTypeOsCount(type, osType)
  }));
};

const typeAnalysisBlocks = computed(() => ['模组/开发板', '芯片', 'OSV'].map((type) => {
  const tableRows = buildTypeRows(type);
  return {
    type,
    tableRows,
    values: tableRows.map((row) => row.count)
  };
}));

const osProductMatrix = computed(() => osTypes.map((osType) => {
  const evalMap = {
    设备: getTypeOsCount('设备', osType),
    '模组/开发板': getTypeOsCount('模组/开发板', osType),
    芯片: getTypeOsCount('芯片', osType),
    OSV: getTypeOsCount('OSV', osType)
  };
  return {
    osType,
    evalMap,
    total: evalMap.设备 + evalMap['模组/开发板'] + evalMap.芯片 + evalMap.OSV
  };
}));

const osProductMatrixSeries = computed(() => [
  { name: '设备', values: osProductMatrix.value.map((item) => item.evalMap.设备), color: evalTypeChartColors[0] },
  { name: '模组/开发板', values: osProductMatrix.value.map((item) => item.evalMap['模组/开发板']), color: evalTypeChartColors[1] },
  { name: '芯片', values: osProductMatrix.value.map((item) => item.evalMap.芯片), color: evalTypeChartColors[2] },
  { name: 'OSV', values: osProductMatrix.value.map((item) => item.evalMap.OSV), color: evalTypeChartColors[3] }
]);

const fetchCategorySummary = async () => {
  summaryLoading.value = true;
  try {
    const params = buildRangeParams();
    const res = await axios.get('/contributorController/getProductCategorySummaryByApprovalTime', { params });
    const list = Array.isArray(res?.data?.data) ? res.data.data : [];
    summaryStats.value = list.filter((item) => item?.certificationType);
  } catch (error) {
    console.error('获取分类汇总失败：', error);
    notification.error({
      message: '获取分类汇总失败',
      description: error?.message || '请稍后再试'
    });
    summaryStats.value = [];
  } finally {
    summaryLoading.value = false;
  }
};

const fetchCategoryDetailStats = async () => {
  detailLoading.value = true;
  try {
    const params = buildRangeParams();
    const res = await axios.get('/contributorController/getProductCategoryDetailStats', { params });
    const payload = res?.data?.data || {};
    detailStats.value = {
      systemTypeStats: Array.isArray(payload.systemTypeStats) ? payload.systemTypeStats : [],
      industrySpecificCategoryStats: Array.isArray(payload.industrySpecificCategoryStats)
        ? payload.industrySpecificCategoryStats
        : []
    };
  } catch (error) {
    console.error('获取分类明细失败：', error);
    notification.error({
      message: '获取分类明细失败',
      description: error?.message || '请稍后再试'
    });
    detailStats.value = {
      systemTypeStats: [],
      industrySpecificCategoryStats: []
    };
  } finally {
    detailLoading.value = false;
  }
};

const refreshAllData = async () => {
  await Promise.all([
    fetchCategorySummary(),
    fetchCategoryDetailStats()
  ]);
};

const formatNumber = (value) => {
  const safeValue = Number.isFinite(Number(value)) ? Number(value) : 0;
  return safeValue.toLocaleString('zh-CN');
};

onMounted(async () => {
  await refreshAllData();
});

watch(
  () => dateRange.value,
  async () => {
    await refreshAllData();
  },
  { deep: true }
);
</script>

<style scoped>
.xts-product-overview-page {
  width: 100%;
  margin-bottom: 64px;
}

.panel {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  margin-bottom: 18px;
}

.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.panel-head h2 {
  margin: 0;
  font-size: 18px;
  color: #1d2129;
}

.panel-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-label {
  font-size: 13px;
  color: #64748b;
}

.total-summary {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 14px;
  color: #475569;
}

.total-summary-label {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin-right: 4px;
}

.total-summary-item {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.total-summary-value {
  font-size: 20px;
  font-weight: 700;
  color: #1f4a8a;
  line-height: 1;
}

.total-summary-unit {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.total-summary-divider {
  color: #cbd5e1;
  font-size: 14px;
  margin: 0 2px;
}

.key-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.key-card {
  --theme-bg-from: #f5f7fb;
  --theme-bg-to: #fafcff;
  --theme-border: #d7ddea;
  --theme-head-bg: #e8edf7;
  --theme-title: #2f3f57;
  --theme-accent: #4b627f;
  --theme-dash: #d7ddea;
  --theme-soft-bg: #f3f6fb;
  --theme-soft-border: #d7ddea;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--theme-bg-from), var(--theme-bg-to));
  border: 1px solid var(--theme-border);
  text-align: left;
  overflow: hidden;
}

.key-card-head {
  padding: 10px 12px;
  font-size: 16px;
  font-weight: 700;
  color: var(--theme-title);
  background: var(--theme-head-bg);
  border-left: 4px solid var(--theme-accent);
  letter-spacing: 0.2px;
}

.metric-pair {
  padding: 8px 12px 10px;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 6px 0;
  border-bottom: 1px dashed var(--theme-dash);
}

.metric-row:last-child {
  border-bottom: 0;
}

.metric-label {
  font-size: 13px;
  color: #334155;
}

.metric-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--theme-accent);
  line-height: 1;
}

.metric-unit {
  margin-left: 2px;
  font-size: 12px;
  color: #475569;
  font-weight: 500;
}

.analysis-grid {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.analysis-grid-secondary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.analysis-card-device {
  width: 100%;
}

.analysis-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  position: relative;
}

.analysis-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  border-radius: 10px 0 0 10px;
  background: var(--theme-accent);
  opacity: 0.9;
}

.analysis-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
}

.card-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;
}

/* Polished header for analysis cards when using key-card look */
.analysis-card.key-card .card-title-row {
  align-items: flex-start;
}

.analysis-card .key-card-head {
  padding: 6px 12px;
  font-size: 15px;
  font-weight: 700;
  color: var(--theme-accent);
  background: var(--theme-head-bg);
  border-left: none;
  border-radius: 6px;
  display: inline-block;
  margin: 0;
}

/* Slightly stronger shadows and cleaner table visuals */
.analysis-card.key-card {
  padding: 16px;
  box-shadow: 0 6px 18px rgba(30, 45, 70, 0.06);
}

.analysis-card .table-wrap {
  border-radius: 8px;
  overflow: hidden;
}

.analysis-card .data-table thead th {
  background: color-mix(in srgb, var(--theme-head-bg) 85%, #ffffff);
  border-bottom: 0;
}

.chart-wrap {
  margin-top: 12px;
  padding-top: 8px;
}

.card-title-row h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--theme-title);
  padding: 0 0 0 10px;
  border-left: 4px solid var(--theme-accent);
  line-height: 1.2;
  letter-spacing: 0.3px;
}

.inline-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
}

.meta-inline {
  margin-bottom: 12px;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 14px;
  color: #475569;
  text-align: left;
  line-height: 1.5;
}

.meta-prefix {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.meta-industry {
  font-size: 18px;
  font-weight: 700;
  color: #243447;
}

.meta-divider {
  color: #94a3b8;
  font-size: 14px;
  margin: 0 2px;
}

.meta-count {
  font-size: 24px;
  font-weight: 800;
  color: #1f4a8a;
  letter-spacing: 0.2px;
  line-height: 1;
}

.meta-unit {
  font-size: 14px;
  color: #475569;
  font-weight: 600;
}

.device-tables-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  align-items: start;
}

.table-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: auto;
  margin-bottom: 10px;
  background: #ffffff;
}

.analysis-card .table-wrap {
  border-color: color-mix(in srgb, var(--theme-accent) 28%, #e2e8f0);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

.device-table-scroll {
  height: 164px;
  overflow-y: auto;
  overflow-x: hidden;
}

.analysis-card .table-wrap.device-table-scroll {
  overflow-y: auto;
  overflow-x: hidden;
}

.device-total-table-scroll {
  height: auto;
  overflow: hidden;
}

.table-wrap.compact {
  max-width: none;
  }

.data-table {
  width: 100%;
  border-collapse: collapse;
  color: #334155;
  min-width: 320px;
}

.data-table th,
.data-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #eef2f7;
  text-align: left;
  white-space: nowrap;
}

.data-table th {
  background: #f8fafc;
  font-weight: 600;
}

.analysis-card .data-table {
  min-width: 0;
}

.analysis-card .data-table th {
  background: var(--theme-head-bg);
  color: var(--theme-title);
  border-bottom-color: color-mix(in srgb, var(--theme-accent) 20%, #eef2f7);
}

.analysis-card .data-table td {
  color: #334155;
}

.analysis-card .data-table tbody tr:hover td {
  background: color-mix(in srgb, var(--theme-accent) 7%, #ffffff);
}

.analysis-card .data-table tbody tr:last-child td {
  border-bottom-color: color-mix(in srgb, var(--theme-accent) 18%, #eef2f7);
}

.os-total-layout {
  display: grid;
  grid-template-columns: 460px 1fr;
  gap: 16px;
  align-items: center;
}

.os-total-table-wrap {
  margin: 24px 0;
  border: 0;
  box-shadow: none;
  background: transparent;
}

.device-total-table-scroll .data-table th,
.device-total-table-scroll .data-table td,
.os-total-table-wrap .data-table th,
.os-total-table-wrap .data-table td {
  padding-top: 12px;
  padding-bottom: 12px;
}

.modern-summary-table {
  min-width: 0;
  border-collapse: separate;
  border-spacing: 0 10px;
}

.modern-summary-table thead th {
  background: #f1f5fb;
  color: #3f4f67;
  border-bottom: 0;
}

.modern-summary-table thead th:first-child {
  border-radius: 8px 0 0 8px;
  text-align: left;
}

.modern-summary-table thead th:last-child {
  border-radius: 0 8px 8px 0;
  text-align: right;
}

.modern-summary-table tbody td {
  background: #ffffff;
  border-top: 1px solid #e7edf6;
  border-bottom: 1px solid #e7edf6;
}

.modern-summary-table tbody td:first-child {
  border-left: 1px solid #e7edf6;
  border-radius: 8px 0 0 8px;
  font-weight: 600;
}

.modern-summary-table tbody td:last-child {
  border-right: 1px solid #e7edf6;
  border-radius: 0 8px 8px 0;
  text-align: right;
  font-size: 16px;
  font-weight: 700;
  color: #305b95;
}

.theme-device {
  --theme-bg-from: #f5f8fb;
  --theme-bg-to: #fbfdff;
  --theme-border: #e1eef9;
  --theme-head-bg: #eef6fc;
  --theme-title: #2b5f86;
  --theme-accent: #4A90E2;
  --theme-dash: #e6eef8;
  --theme-soft-bg: #f7fbff;
  --theme-soft-border: #eaf4fb;
}

.theme-module {
  --theme-bg-from: #f4faf6;
  --theme-bg-to: #fbfff9;
  --theme-border: #e7f6ea;
  --theme-head-bg: #eefbf0;
  --theme-title: #246c3a;
  --theme-accent: #2EB07A;
  --theme-dash: #e9f6ec;
  --theme-soft-bg: #f7fff9;
  --theme-soft-border: #ecf8ee;
}

.theme-chip {
  --theme-bg-from: #f6f4fb;
  --theme-bg-to: #fdfafe;
  --theme-border: #eef0f8;
  --theme-head-bg: #f5f1fb;
  --theme-title: #4a3e70;
  --theme-accent: #8E6FD8;
  --theme-dash: #f3eef8;
  --theme-soft-bg: #fbf8ff;
  --theme-soft-border: #f1eaf8;
}

.theme-osv {
  --theme-bg-from: #fff9f4;
  --theme-bg-to: #fffefb;
  --theme-border: #fff2e6;
  --theme-head-bg: #fff6ee;
  --theme-title: #7a4f22;
  --theme-accent: #E6A23C;
  --theme-dash: #fff3e8;
  --theme-soft-bg: #fff9f5;
  --theme-soft-border: #fff3e6;
}

@media (max-width: 1320px) {
  .key-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .analysis-grid-secondary {
    grid-template-columns: 1fr;
  }

  .device-tables-row {
    grid-template-columns: 1fr;
  }

  .os-total-layout {
    grid-template-columns: 1fr;
  }

  .card-title-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .meta-industry {
    font-size: 16px;
  }

  .meta-count {
    font-size: 22px;
  }
}
</style>
